import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import EchartDoughnutChart from "../../../../Components/GraphNCharts/EchartDoughnut";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import {
  formatDashboardEmissionValue,
  formatNumberScientificCompact,
  formatTruncatedDecimalPlaces,
} from "../../../../utils/common";
import useAuthStore from "../../../../store/ContextUser";
import { get } from "../../../../utils/api";

const COLOR_PALETTE = [
  "#0052CC",
  "#FF3D3D",
  "#FFB000",
  "#37C837",
  "#7B2CBF",
  "#008000",
  "#0000FF",
  "#800080",
  "#808080",
  "#800000",
  "#808000",
  "#008000",
  "#000080",
  "#800080",
  "#808080",
];

const Dashboard = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { selectedCompany } = useAuthStore();

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [dashboardData, setDashboardData] = useState({
    emission: {
      totalCapitalInvestment: 0,
      totalEmissions: 0,
      carbonIntensity: 0,
    },
    categoryAnalysis: { labels: [], dataPoints: [] },
    countryAnalysis: { labels: [], dataPoints: [] },
    topAssets: { labels: [], dataPoints: [] },
  });

  const apiurls = useMemo(
    () => ({
      emission: `/dashboard/scope3/capitalgoods/emission-analysis?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
      emissionByCatoryAndCountry: `/dashboard/scope3/capitalgoods/emissionByCatoryAndCountry?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
    }),
    [fromDate, toDate, selectedFacility, selectedCompany]
  );

  const normalizeEmission = (payload) => {
    const data = payload?.data?.cards || payload?.data || payload?.cards || payload || {};
    return {
      totalCapitalInvestment: Number(data?.totalCapitalInvestment || 0),
      totalEmissions: Number(data?.totalEmissions || 0),
      carbonIntensity:
        data?.carbonIntensity == null ? 0 : Number(data?.carbonIntensity || 0),
    };
  };

  const formatCardMetric = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    if (n === 0) return "0";
    const abs = Math.abs(n);
    if (abs > 0 && abs < 0.1) {
      return formatNumberScientificCompact(n, 2);
    }
    return formatTruncatedDecimalPlaces(n, 3);
  };

  const fetchData = async () => {
    try {
      const [emissionRes, emissionData] = await Promise.all([
        get(apiurls.emission),
        get(apiurls.emissionByCatoryAndCountry),
      ]);

      const emission = normalizeEmission(emissionRes?.data);

      const categoryData =
        emissionData?.data?.totalEmissionByCategory || [];
      const countryData =
        emissionData?.data?.totalEmissionByCountry || [];
      const topCategoryData =
        emissionData?.data?.top5Category || [];

      const topCategory = categoryData.filter(
        (item) => item?.name && Number(item?.totalEmission) > 0
      );
      const topCountry = countryData.filter(
        (item) => item?.name && Number(item?.totalEmission) > 0
      );


      setDashboardData({
        emission,
        categoryAnalysis: {
          labels: topCategory.map((item) => item.name),
          dataPoints: topCategory.map((item) => Number(item.totalEmission || 0)),
        },
        countryAnalysis: {
          labels: topCountry.map((item) => item.name),
          dataPoints: topCountry.map((item) => Number(item.totalEmission || 0)),
        },
        topAssets: { labels: topCategoryData.map((item) => item.name), dataPoints: topCategoryData.map((item) => Number(item.totalEmission || 0)) },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (!selectedCompany?._id) return;
    fetchData();
  }, [fromDate, toDate, selectedFacility, selectedCompany?._id]);

  const generateYearList = (startYear = 2020) => {
    const years = [];
    for (let year = startYear; year <= currentYear; year++) years.push(year);
    return years;
  };


  const yearList = generateYearList(2013);
  const hasCategoryData =
    (dashboardData?.categoryAnalysis?.labels || []).length > 0 &&
    (dashboardData?.categoryAnalysis?.dataPoints || []).length > 0;
  const hasCountryData =
    (dashboardData?.countryAnalysis?.labels || []).length > 0 &&
    (dashboardData?.countryAnalysis?.dataPoints || []).length > 0;

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Capital Goods</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Emission Analysis</p>

        <div className={styles.emission_container}>
          <div className={styles.card_container}>
            <p className={styles.graph_heading}>Total Capital Investment</p>
            <p className={styles.card_value}>
              {formatCardMetric(dashboardData.emission.totalCapitalInvestment)}{" "}
              USD
            </p>
          </div>

          <div className={styles.card_container}>
            <p className={styles.graph_heading}>Total Emissions</p>
            <p className={styles.card_value}>
              {formatCardMetric(dashboardData.emission.totalEmissions)}{" "}
              tCO₂e
            </p>
          </div>

          <div className={styles.card_container}>
            <p className={styles.graph_heading}>Carbon Intensity</p>
            <p className={styles.card_value}>
              {formatCardMetric(dashboardData.emission.carbonIntensity)}{" "}
              tCO₂e/USD
            </p>
          </div>
        </div>

        <div className={styles.doughnutChartRow}>
          <div
            className={`${styles.chart_container} ${styles.doughnutHalfCard}`}
          >
            <p className={styles.graph_heading}>
              Emissions by Capital Goods Category (tCO₂e)
            </p>
            <div className={styles.doughnutContent}>
              <div
                className={
                  hasCategoryData
                    ? styles.doughnutChartSlot
                    : styles.doughnutChartSlotFull
                }
              >
                <EchartDoughnutChart
                  labels={dashboardData?.categoryAnalysis?.labels || []}
                  dataPoints={dashboardData?.categoryAnalysis?.dataPoints || []}
                  colors={COLOR_PALETTE}
                  customHeight="220px"
                  formatDisplayValue={formatDashboardEmissionValue}
                  fixTooltipClipping
                  innerRadius="40%"
                  outerRadius="78%"
                />
              </div>
              {hasCategoryData && (
                <div className={styles.doughnutLegend}>
                  {(dashboardData?.categoryAnalysis?.labels || []).map((label, idx) => (
                    <div
                      key={label}
                      className={styles.doughnutLegendItem}
                    >
                      <div className={styles.doughnutLegendLabel}>
                        <span
                          className={styles.doughnutLegendMarker}
                          style={{ "--legend-color": COLOR_PALETTE[idx] }}
                        />
                        <span className={styles.doughnutLegendText}>
                          {label}
                        </span>
                      </div>
                      <span className={styles.doughnutLegendValue}>
                        {formatDashboardEmissionValue(
                          dashboardData?.categoryAnalysis?.dataPoints?.[idx] ?? 0
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className={`${styles.chart_container} ${styles.doughnutHalfCard}`}
          >
            <p className={styles.graph_heading}>Capital Spent By Country of Origin (USD)</p>
            <div className={styles.doughnutContent}>
              <div
                className={
                  hasCountryData
                    ? styles.doughnutChartSlot
                    : styles.doughnutChartSlotFull
                }
              >
                <EchartDoughnutChart
                  labels={dashboardData?.countryAnalysis?.labels || []}
                  dataPoints={dashboardData?.countryAnalysis?.dataPoints || []}
                  colors={COLOR_PALETTE}
                  customHeight="220px"
                  formatDisplayValue={formatDashboardEmissionValue}
                  fixTooltipClipping
                  innerRadius="40%"
                  outerRadius="78%"
                />
              </div>
              {hasCountryData && (
                <div className={styles.doughnutLegend}>
                  {(dashboardData?.countryAnalysis?.labels || []).map((label, idx) => (
                    <div
                      key={label}
                      className={styles.doughnutLegendItem}
                    >
                      <div className={styles.doughnutLegendLabel}>
                        <span
                          className={styles.doughnutLegendMarker}
                          style={{ "--legend-color": COLOR_PALETTE[idx] }}
                        />
                        <span className={styles.doughnutLegendText}>
                          {label}
                        </span>
                      </div>
                      <span className={styles.doughnutLegendValue}>
                        {formatDashboardEmissionValue(
                          dashboardData?.countryAnalysis?.dataPoints?.[idx] ?? 0
                        )}
                        $
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Top Assets</p>
        <div className={styles.chart_container}>
          <div className={styles.graph_container_header}>
            <p
              className={`${styles.graph_heading} ${styles.graph_heading_bar_ref}`}
            >
              Top 5 Emission-Contributing Assets (tCO₂e)
            </p>
          </div>
          <BarChart
            labels={dashboardData?.topAssets?.labels || []}
            datasets={[
              {
                label: "Emissions",
                data: dashboardData?.topAssets?.dataPoints || [],
                backgroundColor: "rgba(59, 130, 246, 0.95)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 0,
              },
            ]}
            customHeight="260px"
            ellipsis={false}
            linearTickFormatter={formatDashboardEmissionValue}
            horizontalCleanStyle
            valueAxisTitle="Emissions (tCO₂e)"
          />
        </div>
      </div>
    </>
  );
};

export default Dashboard;